import { z } from 'zod';
import { apiGet, apiPost, apiSend } from '@/lib/api';

const PanelTokenRowSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** początek tokenu do rozpoznania — NIE jest sekretem i nie wystarcza do użycia */
  prefix: z.string(),
  createdAt: z.string(),
  createdByEmail: z.string(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  useCount: z.number(),
  /**
   * WYLICZANY po stronie API, nie przechowywany. `NIEUZYWANY` jest osobno od `AKTYWNY`,
   * bo znaczy co innego: token wydany, którego panel nigdy nie użył, to najczęściej token,
   * który nie doszedł tam, gdzie miał dojść.
   */
  status: z.enum(['AKTYWNY', 'ODWOLANY', 'WYGASL', 'NIEUZYWANY']),
  /**
   * ZAKRES — do czego ten token ma prawo na `/api/v1`.
   *
   * ⚠️ MUSI BYĆ W SCHEMACIE, nawet gdyby ekran go nie rysował. Zod odrzuca odpowiedź, której
   * kształtu nie zna, a to już raz kosztowało wieczne „Ładowanie…" na ekranie agentów: API
   * oddawało `200`, front rzucał na walidacji i nie mówiło tego nikomu.
   */
  modules: z.array(z.string()),
  /**
   * ⚠️⚠️ RODZAJ NA LIŚCIE JEST KONIECZNY, NIE OZDOBNY: bez niego ekran nie umie powiedzieć,
   * KTÓRE klucze unieważni wydanie nowego — a wymiana działa per rodzaj. Okno potwierdzenia
   * pokazujące klucz Vending+ przy wydawaniu klucza SmartBoksa kłamałoby w najgorszym kierunku:
   * kazałoby się spodziewać odcięcia cudzej integracji, które nie nastąpi (albo odwrotnie).
   *
   * `optional`, bo instancja starsza od tej zmiany tego pola nie odda — i do 08.10.2026 nie
   * oddawała go ŻADNA (serwis gubił je przy wypisywaniu pól), więc okno SmartBoksa nie widziało
   * kluczy, które unieważniało.
   *
   * ⚠️⚠️ NAPIS, NIE `z.enum`. Sztywna lista wywracała CAŁY ekran przy pierwszym nowym rodzaju —
   * a przy wydaniu pokazywała błąd mimo udanego wydania, czyli token widoczny RAZ przepadał
   * (lekcja „schemat klienta ostrzejszy niż serwis = udany zapis pokazany jako błąd").
   */
  kind: z.string().optional(),
  /** Wyspa klucza przypiętego (HR+); `null` = cała instancja albo wyspa usunięta. */
  groupId: z.string().nullish(),
  groupName: z.string().nullish(),
  /** Rodzaj wymaga wyspy; razem z `groupId: null` = wyspę usunięto i klucz nie działa. */
  islandBound: z.boolean().optional(),
  /** Moduły dozwolone dla rodzaju — okno zmiany zakresu pokazuje tylko te. */
  kindModules: z.array(z.string()).optional(),
});

export type PanelTokenRow = z.infer<typeof PanelTokenRowSchema>;

const ListSchema = z.object({ rows: z.array(PanelTokenRowSchema) });

/**
 * Odpowiedź na utworzenie — JEDYNY moment, w którym plaintext istnieje po naszej stronie.
 * Nie ma endpointu „pokaż token": gdyby był, serwer musiałby przechowywać plaintext.
 */
const CreatedSchema = z.object({ token: z.string(), row: PanelTokenRowSchema });

export const listPanelTokens = () => apiGet('/panel-tokens', ListSchema);

export const createPanelToken = (body: { label: string; expiresAt?: string; modules: string[] }) =>
  apiPost('/panel-tokens', body, CreatedSchema);

export const revokePanelToken = (id: string) => apiPost(`/panel-tokens/${id}/revoke`, {}, PanelTokenRowSchema);

/**
 * TOKEN VENDING+ — pełny zakres integracji, a poprzednie tokeny giną w tej samej transakcji.
 *
 * ⚠️ ZAKRESU NIE WYSYŁAMY. Rozstrzyga o nim serwer (`INTEGRATION_MODULES`) i to jest cała różnica
 * wobec `createPanelToken`: „pełny zakres" wyliczony w przeglądarce znaczyłby „pełny w chwili,
 * gdy zbudowano ten ekran" — po dołożeniu szóstego uprawnienia przycisk cicho wydawałby token
 * bez niego, a lista pokazywałaby dokładnie to, co widać, więc nikt by tego nie zauważył.
 *
 * ⚠️ UNIEWAŻNIENIE JEST CZĘŚCIĄ TEGO JEDNEGO ŻĄDANIA, nie drugim wywołaniem z ekranu. Zerwana
 * sieć między „wydaj" i „odwołaj" zostawiłaby DWA działające tokeny.
 */
const VendingPlusSchema = z.object({
  token: z.string(),
  row: PanelTokenRowSchema,
  uniewaznione: z.array(z.object({ id: z.string(), label: z.string(), prefix: z.string(), useCount: z.number() })),
});

export const wydajTokenVendingPlus = () => apiPost('/panel-tokens/vending-plus', {}, VendingPlusSchema);

/**
 * RODZAJE KLUCZY INTEGRACYJNYCH — katalog trzyma serwer (`panel-tokens/rodzaje.ts`).
 *
 * ⚠️⚠️ EKRAN WYBIERA RODZAJ, A NIE ZAKRES. Zakres i prawo zapisu liczy serwer z rodzaju —
 * gdyby składał je front, „SmartBox" znaczyłoby „to, co ten ekran uważał za SmartBoksa w chwili
 * zbudowania", i po zmianie katalogu przycisk cicho wydawałby klucz o innym zakresie. Ta sama
 * lekcja, która kazała zabrać z tego ekranu pola wyboru uprawnień przy Vending+.
 */
export const RODZAJE_KLUCZY = ['VENDING_PLUS', 'SMARTBOX', 'HR_PLUS'] as const;
export type RodzajKlucza = (typeof RODZAJE_KLUCZY)[number];

/**
 * `groupId` — wyspa klucza przypiętego (HR+). Bez niego serwis bierze jedyną aktywną wyspę,
 * a przy kilku odmawia; przy rodzaju na całą instancję podana wyspa to odmowa.
 */
export const wydajKlucz = (rodzaj: RodzajKlucza, opcje: { label?: string; groupId?: string } = {}) =>
  apiPost('/panel-tokens/wydaj', { rodzaj, ...opcje }, VendingPlusSchema);

/**
 * STAN TOKENU VENDING+ — pod pozycję na pulpicie.
 *
 * ⚠️ TRZY ODPOWIEDZI, NIE DWIE: „brak", „wydany, ale nigdy nie użyty" (sekret nie dojechał do
 * drugiej strony) i „wydany i używany". Pierwsze dwa to dwie różne rzeczy do zrobienia.
 *
 * ⚠️ TRASA JEST TYLKO DLA NASZYCH KONT (admin + SDI), więc pulpit pyta o nią warunkowo — konto
 * klienta nie ma co zrobić z odpowiedzią „brak tokenu".
 */
export const StanVendingPlusSchema = z.object({
  wydany: z.boolean(),
  label: z.string().nullable(),
  prefix: z.string().nullable(),
  wydanoAt: z.string().nullable(),
  ostatnioUzyty: z.string().nullable(),
  uzyc: z.number(),
  wygasa: z.string().nullable(),
  pelnyZakres: z.boolean(),
});
export type StanVendingPlus = z.infer<typeof StanVendingPlusSchema>;

export const fetchStanVendingPlus = () => apiGet('/panel-tokens/vending-plus/stan', StanVendingPlusSchema);

/**
 * ZMIANA ZAKRESU ISTNIEJĄCEGO TOKENU — bez wydawania nowego i bez ruszania sekretu.
 *
 * ⚠️ PO CO TO JEST: sufit integracji rośnie (2026-08-18 doszedł odczyt automatów, planogramu
 * i kartoteki), a token wydany wcześniej ma stary zakres i dostaje `403` na nowych trasach.
 * Wydanie nowego tokenu unieważnia stary w tej samej chwili, więc druga strona musi przekleić
 * sekret — a między jednym a drugim jej integracja stoi. Zmiana zakresu działa NATYCHMIAST
 * i nie wymaga po ich stronie niczego.
 */
export const zmienZakresTokenu = (id: string, modules: string[]) =>
  apiSend('PATCH', `/panel-tokens/${id}/scopes`, { modules }, PanelTokenRowSchema);
